import { useMemo } from 'react';
import {
    PackageCheck,
    TruckFast,
    Clock,
    XCircle,
    Users,
    TrendingUp,
    AlertCircle,
    PackageX,
    Gem,
    Medal,
    Award,
    CalendarDays,
    Calendar,
    Sun,
    Moon
} from 'lucide-react';
import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import InsightCard from '../components/common/InsightCard';
import { useFilters } from '../context/FilterContext';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatCurrency, formatNumber, formatPercentage, formatTimeSlot } from '../utils/formatters';
import styles from './OrderAnalytics.module.css'; // Reusing existing beautiful styles

function DeliveryOperations() {
    const { appliedFilters, orders } = useFilters();

    // 1. Filter the orders globally
    const filteredOrders = useMemo(() => {
        const filters = {
            startDate: appliedFilters.dateRange?.startDate
                ? appliedFilters.dateRange.startDate.toISOString().split('T')[0]
                : null,
            endDate: appliedFilters.dateRange?.endDate
                ? appliedFilters.dateRange.endDate.toISOString().split('T')[0]
                : null,
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters, orders]);

    // 2. Metrics Calculator Helper
    const calculateSegmentMetrics = (orderList) => {
        const totalOrders = orderList.length;
        const totalRevenue = orderList.reduce((sum, o) => sum + o.total, 0);
        const totalVolume = orderList.reduce((sum, o) => sum + (o.volumeKg || 0), 0);
        const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;
        return { orders: totalOrders, revenue: totalRevenue, volume: totalVolume, aov };
    };

    // 3. Section 1: Order & Revenue Metrics
    const totalMetrics = calculateSegmentMetrics(filteredOrders);
    const sameDayOrders = filteredOrders.filter(o => o.deliveryType === 'Same Day');
    const nextDayOrders = filteredOrders.filter(o => o.deliveryType === 'Next Day');
    const sameDayMetrics = calculateSegmentMetrics(sameDayOrders);
    const nextDayMetrics = calculateSegmentMetrics(nextDayOrders);

    // 4. Section 2: Cancellation Metrics
    const cancelledOrders = filteredOrders.filter(o => o.status === 'Cancelled');
    const sameDayCancelled = cancelledOrders.filter(o => o.deliveryType === 'Same Day');
    const nextDayCancelled = cancelledOrders.filter(o => o.deliveryType === 'Next Day');
    const sameDayCancelMetrics = calculateSegmentMetrics(sameDayCancelled);
    const nextDayCancelMetrics = calculateSegmentMetrics(nextDayCancelled);

    // Cancellation Reasons
    const cancelDelayCount = cancelledOrders.filter(o => o.cancelReason === 'Due to delay').length;
    const cancelOosCount = cancelledOrders.filter(o => o.cancelReason === 'Due to OOS').length;

    // FREQUENT OOS SKUS
    const oosSkusMap = {};
    cancelledOrders.filter(o => o.cancelReason === 'Due to OOS').forEach(o => {
        o.items.forEach(item => {
            if (!oosSkusMap[item.productName]) {
                oosSkusMap[item.productName] = { name: item.productName, count: 0, revenueImpact: 0 };
            }
            oosSkusMap[item.productName].count += item.quantity;
            oosSkusMap[item.productName].revenueImpact += item.total;
        });
    });
    const topOosSkus = Object.values(oosSkusMap).sort((a, b) => b.count - a.count).slice(0, 5);

    // 5. Section 3: Delay Metrics
    const delayedOrders = filteredOrders.filter(o => o.isDelayed && o.status !== 'Cancelled');
    const sameDayDelayed = delayedOrders.filter(o => o.deliveryType === 'Same Day');
    const nextDayDelayed = delayedOrders.filter(o => o.deliveryType === 'Next Day');
    const sameDayDelayMetrics = calculateSegmentMetrics(sameDayDelayed);
    const nextDayDelayMetrics = calculateSegmentMetrics(nextDayDelayed);

    // 6. Section 4: Customer Segmentation
    // Segment Logic: Diamond (>4 orders), Gold (2-4 orders), Silver (1 order)
    const customerMap = {};
    filteredOrders.forEach(o => {
        if (!customerMap[o.customerId]) {
            customerMap[o.customerId] = {
                id: o.customerId,
                name: o.customerName,
                orders: 0,
                revenue: 0,
                volume: 0,
                purchaseDates: new Set()
            };
        }
        customerMap[o.customerId].orders += 1;
        customerMap[o.customerId].revenue += o.total;
        customerMap[o.customerId].volume += (o.volumeKg || 0);
        customerMap[o.customerId].purchaseDates.add(o.date);
    });

    const segments = { diamond: [], gold: [], silver: [] };
    Object.values(customerMap).forEach(c => {
        if (c.orders > 4) segments.diamond.push(c);
        else if (c.orders >= 2) segments.gold.push(c);
        else segments.silver.push(c);
    });

    const calculateCustomerSegment = (customerArray) => {
        if (customerArray.length === 0) return { count: 0, orders: 0, revenue: 0, volume: 0, aov: 0, repeatRate: 0 };
        const totalO = customerArray.reduce((sum, c) => sum + c.orders, 0);
        const totalR = customerArray.reduce((sum, c) => sum + c.revenue, 0);
        const totalV = customerArray.reduce((sum, c) => sum + c.volume, 0);
        const totalPurchaseDays = customerArray.reduce((sum, c) => sum + c.purchaseDates.size, 0);

        // Formula: No. of days purchased / total no. of days in a month (30)
        // Average across the segment customers.
        const avgPurchaseDays = totalPurchaseDays / customerArray.length;
        const repeatRate = Math.min((avgPurchaseDays / 30) * 100, 100);

        return {
            count: customerArray.length,
            orders: totalO,
            revenue: totalR,
            volume: totalV,
            aov: totalO > 0 ? totalR / totalO : 0,
            repeatRate
        };
    };

    const diamondMetrics = calculateCustomerSegment(segments.diamond);
    const goldMetrics = calculateCustomerSegment(segments.gold);
    const silverMetrics = calculateCustomerSegment(segments.silver);

    // 7. Table Columns for Raw Export
    const rawExportColumns = [
        { key: 'id', label: 'Order ID', width: '200px' },
        { key: 'date', label: 'Date' },
        { key: 'deliveryType', label: 'Delivery Type' },
        { key: 'timeSlotId', label: 'Time Slot', render: (v, row) => formatTimeSlot(row._original?.TimeSlotId || v) },
        { key: 'status', label: 'Status' },
        { key: 'customerName', label: 'Customer' },
        { key: 'grade', label: 'Grade' },
        { key: 'cityName', label: 'City' },
        { key: 'storeName', label: 'Store' },
        { key: 'paymentMethod', label: 'Payment Method' },
        { key: 'paymentGateway', label: 'Payment Gateway' },
        { key: 'itemCount', label: 'Items' },
        { key: 'volumeKg', label: 'Volume (KG)' },
        { key: 'total', label: 'Total Value', render: (v) => formatCurrency(v) },
        { key: 'deliveryScheduleDate', label: 'Delivery Schedule Date' },
        { key: 'pickUpDate', label: 'Pick Up Date' },
        { key: 'deliveryDate', label: 'Actual Delivery Date' },
        { key: 'orderBillDate', label: 'Bill Date' },
        { key: 'orderBillNo', label: 'Bill No' },
        { key: 'cancelReason', label: 'Cancel Reason', render: (v) => v || '-' },
    ];

    // RENDER HELPERS
    const SegmentCard = ({ title, m, icon: Icon, colorCode }) => (
        <div style={{
            backgroundColor: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-5)',
            border: '1px solid var(--color-border)',
            borderTop: `4px solid ${colorCode}`,
            boxShadow: 'var(--box-shadow-sm)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                <div style={{ padding: '10px', borderRadius: '12px', backgroundColor: `${colorCode}15`, color: colorCode }}>
                    <Icon size={22} />
                </div>
                <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--color-text)', margin: 0 }}>{title}</h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)' }}>
                <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Orders</div>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-text)' }}>{formatNumber(m.orders)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Revenue</div>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-text)' }}>{formatCurrency(m.revenue, { compact: true })}</div>
                </div>
                <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Volume</div>
                    <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--color-text)' }}>{formatNumber(m.volume)} <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', fontWeight: 500 }}>KG</span></div>
                </div>
                <div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg Order Val</div>
                    <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--color-text)' }}>{formatCurrency(m.aov)}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Delivery Operations Dashboard</h1>
                <p className={styles.subtitle}>Deep dive into operational SLAs, Cancellations, and Cohorts</p>
            </header>

            {/* 1. Order & Revenue Metrics */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <TrendingUp size={20} className={styles.sectionIcon} style={{ color: 'var(--color-primary)' }} />
                    1. Order & Revenue Metrics
                </h2>
                <div className={styles.kpiGrid3}>
                    <SegmentCard title="Total (All Orders)" m={totalMetrics} icon={CalendarDays} colorCode="#3B82F6" />
                    <SegmentCard title="Same Day Delivery" m={sameDayMetrics} icon={Sun} colorCode="#10B981" />
                    <SegmentCard title="Next Day Delivery" m={nextDayMetrics} icon={Moon} colorCode="#8B5CF6" />
                </div>
            </section>

            {/* 2. Cancellation Metrics */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <XCircle size={20} className={styles.sectionIcon} style={{ color: 'var(--color-error)' }} />
                    2. Cancellation Metrics
                </h2>
                <div className={styles.kpiGrid3}>
                    <SegmentCard title="Same Day Cancelled" m={sameDayCancelMetrics} icon={Sun} colorCode="#EF4444" />
                    <SegmentCard title="Next Day Cancelled" m={nextDayCancelMetrics} icon={Moon} colorCode="#F59E0B" />

                    <div style={{ backgroundColor: 'var(--color-card)', padding: '1.5rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
                        <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, marginBottom: '1.2rem', color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AlertCircle size={18} color="var(--color-warning)" /> Cancellation Reasons
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ padding: '1rem', backgroundColor: 'var(--color-background)', borderRadius: '8px', borderLeft: '4px solid var(--color-warning)' }}>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Due to Delay</div>
                                <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-warning)' }}>{formatNumber(cancelDelayCount)}</div>
                            </div>
                            <div style={{ padding: '1rem', backgroundColor: 'var(--color-background)', borderRadius: '8px', borderLeft: '4px solid var(--color-error)' }}>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: '4px' }}>Due to OOS</div>
                                <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, color: 'var(--color-error)' }}>{formatNumber(cancelOosCount)}</div>
                            </div>
                        </div>

                        <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <PackageX size={14} /> Top OOS SKUs Impact
                        </h4>
                        {topOosSkus.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {topOosSkus.map((sku, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: idx < topOosSkus.length - 1 ? '1px dashed var(--color-border)' : 'none' }}>
                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 500 }}>{sku.name.length > 25 ? sku.name.slice(0, 25) + '...' : sku.name}</span>
                                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-error)' }}>{sku.count} <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>units</span></span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ padding: '1rem', textAlign: 'center', backgroundColor: 'var(--color-background)', borderRadius: '8px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}>
                                No Out of Stock data available in this period
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* 3. Delay Metrics */}
            <section className={styles.section}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-4)' }}>
                    <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
                        <Clock size={20} className={styles.sectionIcon} style={{ color: 'var(--color-warning)' }} />
                        3. Delay Metrics
                    </h2>
                    <div style={{ padding: '6px 16px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: 'var(--color-warning)', borderRadius: '20px', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                        Total Delayed Orders: {formatNumber(delayedOrders.length)}
                    </div>
                </div>
                <div className={styles.kpiGrid3}>
                    <SegmentCard title="Same Day Delayed" m={sameDayDelayMetrics} icon={Sun} colorCode="#F59E0B" />
                    <SegmentCard title="Next Day Delayed" m={nextDayDelayMetrics} icon={Moon} colorCode="#F59E0B" />
                </div>
            </section>

            {/* 4. Customer Segmentation */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>
                    <Users size={20} className={styles.sectionIcon} style={{ color: 'var(--color-success)' }} />
                    4. Customer Segmentation (Monthly Basis)
                </h2>
                <div className={styles.kpiGrid3}>

                    {/* Diamond */}
                    <div style={{ backgroundColor: 'var(--color-card)', padding: 'var(--spacing-5)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', borderTop: '4px solid #3B82F6', boxShadow: 'var(--box-shadow-sm)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#3B82F6' }}>
                                <div style={{ padding: '8px', backgroundColor: '#3B82F615', borderRadius: '8px' }}><Gem size={20} /></div> Diamond
                            </h3>
                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-background)', padding: '4px 10px', borderRadius: '12px' }}>Frequent Buyer</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: 'var(--font-size-sm)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Customers:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(diamondMetrics.count)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Orders:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(diamondMetrics.orders)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Revenue:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatCurrency(diamondMetrics.revenue, { compact: true })}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Volume (KG):</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(diamondMetrics.volume)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>AOV:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatCurrency(diamondMetrics.aov)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '12px', borderTop: '1px dashed var(--color-border)' }}>
                                <span style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}>Repeat Rate:</span>
                                <strong style={{ color: '#3B82F6', fontSize: 'var(--font-size-lg)' }}>{formatPercentage(diamondMetrics.repeatRate)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* Gold */}
                    <div style={{ backgroundColor: 'var(--color-card)', padding: 'var(--spacing-5)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', borderTop: '4px solid #F59E0B', boxShadow: 'var(--box-shadow-sm)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#F59E0B' }}>
                                <div style={{ padding: '8px', backgroundColor: '#F59E0B15', borderRadius: '8px' }}><Award size={20} /></div> Gold
                            </h3>
                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-background)', padding: '4px 10px', borderRadius: '12px' }}>Moderate Buyer</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: 'var(--font-size-sm)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Customers:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(goldMetrics.count)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Orders:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(goldMetrics.orders)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Revenue:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatCurrency(goldMetrics.revenue, { compact: true })}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Volume (KG):</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(goldMetrics.volume)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>AOV:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatCurrency(goldMetrics.aov)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '12px', borderTop: '1px dashed var(--color-border)' }}>
                                <span style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}>Repeat Rate:</span>
                                <strong style={{ color: '#F59E0B', fontSize: 'var(--font-size-lg)' }}>{formatPercentage(goldMetrics.repeatRate)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* Silver */}
                    <div style={{ backgroundColor: 'var(--color-card)', padding: 'var(--spacing-5)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', borderTop: '4px solid #6B7280', boxShadow: 'var(--box-shadow-sm)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280' }}>
                                <div style={{ padding: '8px', backgroundColor: '#6B728015', borderRadius: '8px' }}><Medal size={20} /></div> Silver
                            </h3>
                            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-background)', padding: '4px 10px', borderRadius: '12px' }}>One-time Buyer</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: 'var(--font-size-sm)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Customers:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(silverMetrics.count)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Orders:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(silverMetrics.orders)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Revenue:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatCurrency(silverMetrics.revenue, { compact: true })}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>Volume (KG):</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatNumber(silverMetrics.volume)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-secondary)' }}>AOV:</span> <strong style={{ fontSize: 'var(--font-size-md)' }}>{formatCurrency(silverMetrics.aov)}</strong></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '12px', borderTop: '1px dashed var(--color-border)' }}>
                                <span style={{ fontWeight: 500, color: 'var(--color-text-secondary)' }}>Repeat Rate:</span>
                                <strong style={{ color: '#6B7280', fontSize: 'var(--font-size-lg)' }}>{formatPercentage(silverMetrics.repeatRate)}</strong>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* 5. Raw Export Table */}
            <section className={styles.section}>
                <DataTable
                    data={filteredOrders}
                    columns={rawExportColumns}
                    title="Raw Delivery Operations Export"
                    icon={PackageCheck}
                    exportFilename="delivery_operations_export"
                    defaultPageSize={10}
                    exportData={(data) => data.map(row => ({
                        ...row,
                        timeSlotId: formatTimeSlot(row._original?.TimeSlotId || row.timeSlotId),
                    }))}
                />
            </section>

        </div>
    );
}

export default DeliveryOperations;
